import { useState } from 'react';
import { LoadFailed } from '../components/QueryState';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { canAuthorAutomations } from '@promanage/core';
import { describeConditionNode } from '@promanage/core/automation';
import { Button, Chip, Panel, PanelHeader } from '@promanage/ui';
import { Icon } from '../components/Icon';
import { RecordIdentity } from '../components/RecordIdentity';
import { RecordNotFound } from '../components/RecordNotFound';
import { RecordSkeleton } from '../components/RecordSkeleton';
import { TopBar } from '../components/TopBar';
import { errorCode } from '../lib/errors';
import { useUnsavedChanges } from '../components/UnsavedChanges';
import { useSession } from '../features/auth/session';
import { RunLog } from '../features/automations/RunLog';
import { RuleForm, type RuleDraft } from '../features/automations/RuleForm';
import { activityText, ruleState, whenText } from '../features/automations/format';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/automations/$id')({
  component: AutomationPage,
});

/**
 * One automation: what it says it does, and what it has actually done.
 *
 * THE RUN LOG IS THE PAGE, not an appendix to it. M5's exit criterion is that an agency can see a
 * rule fire "and see why it did not fire when it did not", and that question is always asked about
 * ONE rule — so the whole-agency log on the index is the browse and this is the answer.
 *
 * EDITING HAPPENS IN PLACE, on the same form that authored it. A separate edit route would be a
 * second rendering of the same fields, and the two would eventually disagree about which action
 * needs which configuration.
 */
function AutomationPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const unsaved = useUnsavedChanges();
  const queryClient = useQueryClient();
  const session = useSession();
  const mayAuthor = canAuthorAutomations(session?.roles ?? []);

  const rule = useQuery(
    trpc.automation.get.queryOptions(
      { id },
      { meta: { errorTitle: 'Could not load that automation' }, staleTime: 10_000 },
    ),
  );
  // Whether the agency has stopped everything — the rule's own chip has to say so, because a rule
  // that is switched on inside a paused agency is not running and must not read as though it is.
  const overview = useQuery(
    trpc.automation.overview.queryOptions(undefined, { staleTime: 30_000 }),
  );

  const [draft, setDraft] = useState<RuleDraft | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: trpc.automation.get.queryKey({ id }) });
    void queryClient.invalidateQueries({ queryKey: trpc.automation.overview.queryKey() });
  };

  const setEnabled = useMutation(
    trpc.automation.setEnabled.mutationOptions({
      meta: { errorTitle: 'Could not change this automation’s status' },
      onSuccess: invalidate,
    }),
  );
  const update = useMutation(
    trpc.automation.update.mutationOptions({
      meta: { errorTitle: 'Could not save that automation' },
      onSuccess() {
        unsaved.saved();
        invalidate();
        setDraft(null);
      },
    }),
  );
  const remove = useMutation(
    trpc.automation.delete.mutationOptions({
      meta: { errorTitle: 'Could not delete that automation' },
      onSuccess() {
        invalidate();
        void navigate({ to: '/automations' });
      },
    }),
  );

  if (rule.isPending) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="Automation" subtitle="Loading…" />
        <RecordSkeleton />
      </div>
    );
  }

  /*
   * A MISSING RULE AND A FAILED LOAD ARE DIFFERENT ANSWERS, as on an inspection: "No such
   * automation" about one whose request merely dropped would send somebody to rebuild a rule that
   * is still running.
   */
  if (errorCode(rule.error) === 'NOT_FOUND') {
    return (
      <RecordNotFound
        record="Automation"
        title="No such automation"
        back={{ label: 'Back to automations', link: { to: '/automations' } }}
      >
        It may have been deleted. Its runs are still in the log on the automations page — every run
        kept its own copy of what the rule said.
      </RecordNotFound>
    );
  }

  if (rule.isError) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="Automation" />
        <Panel className="p-6">
          <LoadFailed inline retry={() => void rule.refetch()} retrying={rule.isFetching} />
        </Panel>
      </div>
    );
  }

  const data = rule.data;
  const state = ruleState(data, overview.data?.paused ?? false);
  const conditions = data.conditions;

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Automation"
        subtitle={activityText(data, overview.data?.activityDays ?? 30)}
        actions={
          <>
            <Link to="/automations">
              <Button size="sm" variant="secondary">
                All automations
              </Button>
            </Link>
            {mayAuthor && draft === null ? (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={setEnabled.isPending}
                  onClick={() => setEnabled.mutate({ id, enabled: !data.enabled })}
                >
                  {data.enabled ? 'Switch off' : 'Switch on'}
                </Button>
                <Button
                  size="sm"
                  onClick={() =>
                    setDraft({
                      name: data.name,
                      triggerEvent: data.triggerEvent,
                      conditions: conditions,
                      action: data.action,
                      config: data.config as RuleDraft['config'],
                    })
                  }
                >
                  Edit
                </Button>
              </>
            ) : null}
          </>
        }
      />

      <div className="flex flex-col gap-4">
        {draft !== null ? (
          <RuleForm
            draft={draft}
            onChange={(next) => {
              unsaved.touch();
              setDraft(next);
            }}
            onSubmit={(input) => update.mutate({ ...input, id })}
            onCancel={() => {
              unsaved.reset();
              setDraft(null);
            }}
            submitLabel="Save changes"
            pending={update.isPending}
          />
        ) : null}

        {draft === null ? (
          <>
            <RecordIdentity
              title={data.name}
              symbol={<Icon name="automation" className="size-6" />}
              status={<Chip tone={state.tone}>{state.label}</Chip>}
              subtitle={
                <>
                  <span className="font-semibold text-ink">{data.sentence}</span>
                  {data.runnable ? null : (
                    <span className="mt-1 block text-warn">{data.because}</span>
                  )}
                </>
              }
              facts={[
                {
                  label: 'Trigger',
                  value: (
                    <>
                      {data.triggerLabel}
                      <span className="mt-0.5 block font-mono text-[0.75rem] font-normal text-ink-subtle">
                        {data.triggerEvent}
                      </span>
                    </>
                  ),
                },
                {
                  label: 'Last fired',
                  value: (
                    <>
                      {whenText(data.lastRunAt)}
                      <span className="mt-0.5 block text-[0.75rem] font-normal text-ink-subtle">
                        Changed {whenText(data.updatedAt)}
                      </span>
                    </>
                  ),
                },
                {
                  label: 'Created',
                  value: (
                    <>
                      {data.createdByName ?? 'Somebody'}
                      <span className="mt-0.5 block text-[0.75rem] font-normal text-ink-subtle">
                        {whenText(data.createdAt)}
                      </span>
                    </>
                  ),
                },
              ]}
            />

            {state.detail ? (
              <p className="rounded-md bg-danger-soft px-4 py-3 text-[0.8125rem] font-medium text-danger">
                {state.detail}
              </p>
            ) : null}

            <Panel>
              <PanelHeader title="Conditions" subtitle="What it checks before it acts" />
              <div className="border-t border-rule px-5 py-4 text-[0.875rem]">
                {conditions.length === 0 ? (
                  <span className="text-ink-muted">
                    None — it acts on every occurrence of the trigger.
                  </span>
                ) : (
                  <ul className="flex flex-col gap-1">
                    {conditions.map((condition, index) => (
                      <li key={index}>{describeConditionNode(condition)}</li>
                    ))}
                  </ul>
                )}
              </div>
            </Panel>

            <RunLog
              ruleId={id}
              title="This automation’s history"
              subtitle="Every event it saw, including the ones it did not match"
            />

            {mayAuthor ? (
              <DangerZone onDelete={() => remove.mutate({ id })} pending={remove.isPending} />
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Deleting one.
 *
 * BEHIND A CONFIRMATION, and the sentence beside it is the one that matters: the run log survives.
 * An operator hesitating here is usually worried about losing the evidence of what the rule did,
 * and the answer is that they will not — every run carries its own copy of the rule's name and
 * sentence, which is why deleting is safe rather than merely permitted.
 */
function DangerZone({ onDelete, pending }: { onDelete: () => void; pending: boolean }) {
  const [confirming, setConfirming] = useState(false);

  return (
    <Panel className="px-5 py-4">
      <p className="text-[0.875rem] font-semibold">Delete this automation</p>
      <p className="mt-1 max-w-prose text-[0.8125rem] text-ink-muted">
        It stops immediately and disappears from the list. Its history stays in the run log — every
        run recorded what the rule said at the time it fired, so the record of what happened is not
        lost with the rule. If you only want it to stop for now, switch it off instead.
      </p>

      <div className="mt-3 flex gap-2">
        {confirming ? (
          <>
            <Button size="sm" variant="danger" disabled={pending} onClick={onDelete}>
              {pending ? 'Deleting' : 'Yes, delete it'}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setConfirming(false)}>
              Keep it
            </Button>
          </>
        ) : (
          <Button size="sm" variant="secondary" onClick={() => setConfirming(true)}>
            Delete
          </Button>
        )}
      </div>
    </Panel>
  );
}
